import { TFunction, i18n } from "i18next";
import { Alaska } from "./Alaska";
import { DistrictOfColumbia } from "./DistrictOfColumbia";
import { Idaho } from "./Idaho";
import { Maine } from "./Maine";
import { Minnesota } from "./Minnesota";
import { Missouri } from "./Missouri";
import { NewJersey } from "./NewJersey";
import { NorthCarolina } from "./NorthCarolina";
import { NorthDakota } from "./NorthDakota";
import { Utah } from "./Utah";
import { Wyoming } from "./Wyoming";

/*
  "records_status_overdue": "overdue",
  "records_status_immune": "immune",
  "records_status_invalid": "invalid",
  "records_status_due_now": "due now",
  "records_status_current": "current",
  "records_status_complete": "complete",
  "records_status_record": "record",
  "records_status_no_record": "no record",
  "records_status_due": "due",
  "records_status": "record",
  These are fed into t() as a suffix for the translation strings.
*/
export type TRANSLATION_STATUS_LABEL =
  | "overdue"
  | "due_now"
  | "due"
  | "current"
  | "complete"
  | "immune"
  | "no_record"
  | "processing" // TODO: i18n for this string
  | "record"
  | "unknown"; // TODO: i18n for this string

/**
 * The provider interface gives all the 'important' stuff a provider needs to
 * have filled out to function in the app.
 */
export interface IzProvider {
  /**
   * The full name of the government department that hosts the data, e.g. "New Jersey Department of Health"
   */
  governmentDepartmentName: string;

  /**
   * The title of the disclaimer card, shown inside of a record set.
   */
  recordDisclaimerTitle: string;

  /**
   * The body text for the disclaimer card, shown inside of a record set.
   * This may be an empty string.
   */
  recordDisclaimerText: string;

  /**
   * The body text for the legal disclaimer to use this data shown on the new search page.
   * This may be an empty string.
   */
  searchLegalText: string;

  /**
   * Short-form of the IIS name, e.g. NJIIS
   */
  iisAbbrev: string;

  /**
   * The "full name" of the IIS, such as New Jersey Immunization Information System
   */
  iisName: string;

  /**
   * Typically the 2-letter code of the state, e.g. NJ
   */
  jurisdictionAbbrev: string;

  /**
   * Typically the full name of the state, e.g. New Jersey
   */
  jurisdictionName: string;

  /**
   * The image to show during Scenario B (needs more verification)
   */
  scenarioBImagePath: string;

  /**
   * The status images and settings for this IIS.
   */
  statusImages: IzProviderStatusImageSettings;

  /**
   * Report titles for this provider
   */
  reportTitles: string[];

  /**
   * URL to provide screening test information
   */
  screeningResultLink: string;
}

export interface IzProviderStatusImageSettings {
  /**
   * Traditionally the "DUE_NOW" label is yellow.
   *
   * Should this IZ provider show red labels when the status in "DUE_NOW"?
   * This should also apply to the QR code icons.
   */
  redOnDue: boolean;

  /**
   * True if the "NO RECORD" image should be shown for forecasted shots, false otherwise.
   */
  useNoRecordImage: boolean;

  /**
   * A path to the 'OVERDUE' image used to label immunization series.
   */
  overdueImagePath: string;

  /**
   * A path to the 'DUE NOW' (or DUE) image used to label immunization series.
   */
  dueNowImagePath: string;

  /**
   * A path to the 'CURRENT' image used to label immunization series.
   */
  currentImagePath: string;

  /**
   * A path to the 'COMPLETE' image used to label immunization series.
   */
  completeImagePath: string;

  /**
   * A path to the 'IMMUNE' image used to label immunization series.
   */
  immuneImagePath: string;

  /**
   * A path to the 'RECORD' image used to label immunization series.
   */
  recordImagePath: string;

  /**
   * A path to the 'NO RECORD' image used to label immunization series.
   */
  noRecordImagePath: string;

  /**
   * A path to the 'NO RECORD' image used to label immunization series.
   */
  processingImagePath: string;
  /**
   * A path to the 'OVERDUE' image used to label immunization series.
   */
  overdueLabel: TRANSLATION_STATUS_LABEL;

  /**
   * A path to the 'DUE NOW' (or DUE) image used to label immunization series.
   */
  dueNowLabel: TRANSLATION_STATUS_LABEL;

  /**
   * A path to the 'CURRENT' image used to label immunization series.
   */
  currentLabel: TRANSLATION_STATUS_LABEL;

  /**
   * A path to the 'COMPLETE' image used to label immunization series.
   */
  completeLabel: TRANSLATION_STATUS_LABEL;

  /**
   * A path to the 'IMMUNE' image used to label immunization series.
   */
  immuneLabel: TRANSLATION_STATUS_LABEL;

  /**
   * A path to the 'RECORD' image used to label immunization series.
   */
  recordLabel: TRANSLATION_STATUS_LABEL;

  /**
   * A path to the 'NO RECORD' image used to label immunization series.
   */
  noRecordLabel: TRANSLATION_STATUS_LABEL;

  /**
   * A path to the 'NO RECORD' image used to label immunization series.
   */
  processingLabel: TRANSLATION_STATUS_LABEL;
}

export type ProviderKey =
  | "ak"
  | "dc"
  | "id"
  | "me"
  | "mn"
  | "mo"
  | "nc"
  | "nd"
  | "nj"
  | "ut"
  | "wy";

type ProviderConstructor = new (id: ProviderKey, t: TFunction) => IzProvider;
const PROVIDER_MAPPING: Record<ProviderKey, ProviderConstructor> = {
  ak: Alaska,
  dc: DistrictOfColumbia,
  id: Idaho,
  me: Maine,
  mn: Minnesota,
  mo: Missouri,
  nc: NorthCarolina,
  nd: NorthDakota,
  nj: NewJersey,
  ut: Utah,
  wy: Wyoming,
} as const;

/**
 * Get the appropriate IZ provider class to get all the correct settings.
 *
 * @param izDestinationId The jurisdiction destination ID, e.g. "nj", "ak", etc.
 * @param t The translator function from react-i18next after calling useTranslation()
 * @returns An IzProvider with all required strings for the jurisdiction, or the default provider if a matching provider could not be found.
 */
export function getIzProvider(izDestinationId: ProviderKey, t: TFunction): IzProvider {
  const lowerIzDestinationId = izDestinationId.toLowerCase();
  const providerClass = PROVIDER_MAPPING[lowerIzDestinationId as ProviderKey];
  if (providerClass) {
    return new providerClass(izDestinationId, t);
  }
  throw new Error(`No IZ provider found for destination ID: ${izDestinationId}`);
}

export default getIzProvider;
